// VAPID (RFC 8292) for Web Push — mints the ES256 JWT that authenticates the
// server to the push services. Pure WebCrypto, no dependencies; runs on
// Cloudflare Workers and Node 20+.
//
// Key format = what `npx web-push generate-vapid-keys` produces:
//  * public key: base64url, 65-byte uncompressed P-256 point (0x04 ‖ x ‖ y)
//  * private key: base64url, 32-byte scalar d
// The JWK needed by WebCrypto is derived from those two values here.

import { toBase64Url, fromBase64Url } from './base64url.js';

const encoder = new TextEncoder();

export interface VapidKeys {
  /** base64url 65-byte uncompressed public key (also used by the browser to subscribe). */
  publicKey: string;
  /** base64url 32-byte private scalar. */
  privateKey: string;
  /** Contact for the push service, e.g. 'mailto:hotel@lemontaigu.com'. */
  subject: string;
}

async function importVapidPrivateKey(keys: VapidKeys): Promise<CryptoKey> {
  const pub = fromBase64Url(keys.publicKey);
  if (pub.length !== 65 || pub[0] !== 0x04) {
    throw new Error('vapid: public key must be a 65-byte uncompressed P-256 point');
  }
  const jwk: JsonWebKey = {
    kty: 'EC',
    crv: 'P-256',
    x: toBase64Url(pub.slice(1, 33)),
    y: toBase64Url(pub.slice(33, 65)),
    d: keys.privateKey,
  };
  return crypto.subtle.importKey('jwk', jwk, { name: 'ECDSA', namedCurve: 'P-256' }, false, [
    'sign',
  ]);
}

/**
 * ES256 JWT for `audience` (the push-service origin). 12h validity — mint per
 * send; push services cap at 24h.
 */
export async function mintVapidJwt(
  keys: VapidKeys,
  audience: string,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): Promise<string> {
  const head = toBase64Url(encoder.encode(JSON.stringify({ typ: 'JWT', alg: 'ES256' })));
  const body = toBase64Url(
    encoder.encode(
      JSON.stringify({ aud: audience, exp: nowSeconds + 12 * 3600, sub: keys.subject }),
    ),
  );
  const key = await importVapidPrivateKey(keys);
  // WebCrypto ECDSA yields the raw r‖s (64-byte) signature — the JWS ES256 format.
  const sig = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    key,
    encoder.encode(`${head}.${body}`),
  );
  return `${head}.${body}.${toBase64Url(new Uint8Array(sig))}`;
}

/** `Authorization` header value for a Web Push request (RFC 8292 §3). */
export async function vapidAuthorizationHeader(
  keys: VapidKeys,
  endpoint: string,
  nowSeconds?: number,
): Promise<string> {
  const audience = new URL(endpoint).origin;
  const jwt = await mintVapidJwt(keys, audience, nowSeconds);
  return `vapid t=${jwt}, k=${keys.publicKey}`;
}
